import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Calendar, Repeat, Video, Trash2, List, CalendarDays, Link2, MessageCircle, Mail, UserPlus, Share2, Zap, DoorOpen, Search, Pencil, Globe } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Calendar as BigCalendar, dateFnsLocalizer } from "react-big-calendar";
import { format, parse, startOfWeek, getDay } from "date-fns";
import enUS from "date-fns/locale/en-US";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { SubjectPicker } from "@/components/CurriculumPicker";
import { CtaButton } from "@/components/ui/CtaButton";
import { InstantClassModal } from "@/components/live/InstantClassModal";
import { openMeetTracked, autoEndClass } from "@/lib/meetWindow";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { tzOptions, browserTz, zonedToUtcIso, utcToZoned, todayInTz } from "@/lib/tzUtils";
import { fmtDate, fmtDateTime } from "@/lib/dateUtils";
import { DateField } from "@/components/ui/DateField";
import { usePager, Pager } from "@/components/ui/TablePager";

const locales = { "en-US": enUS };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales });

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
// Day chips shown starting from TODAY in the given timezone (e.g. on a Thursday: THU FRI SAT … WED)
const orderedDaysFor = (tz) => {
  const idx = (todayInTz(tz).jsDay + 6) % 7; // monday=0
  return [...DAYS.slice(idx), ...DAYS.slice(0, idx)];
};
const platformLabel = (p) => (p === "google_meet" || p === "meet" ? "Google Meet" : p === "jitsi" || !p ? "TS Platform" : p);

export default function ClassesPage({ studentView }) {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [view, setView] = useState("table"); // table | calendar
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [editClass, setEditClass] = useState(null);
  const [instant, setInstant] = useState(null); // { class, meet_url } → modal
  const [startingInstant, setStartingInstant] = useState(false);

  // Instant Google Meet — closing the Meet tab auto-ends the class.
  const startInstantClass = async () => {
    if (startingInstant) return;
    setStartingInstant(true);
    try {
      const { data } = await api.post("/classes/instant", { duration_minutes: 60 });
      setInstant(data);
      openMeetTracked(data.meet_url, () => autoEndClass(data.class.id, { onDone: () => { setInstant(null); load(); } }), data.class.id);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start your class");
    } finally {
      setStartingInstant(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/classes");
      setClasses(data.classes || []);
      if (!studentView) {
        try {
          const r = await api.get("/students");
          setStudents(r.data.students || []);
        } catch { /* admins won't have access — ignore */ }
      }
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this class?")) return;
    try { await api.delete(`/classes/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const sortedClasses = useMemo(() => {
    const s = search.trim().toLowerCase();
    return [...classes]
      .filter((c) => !s || `${c.title} ${c.subject} ${c.status} ${c.meeting_platform} ${c.class_type}`.toLowerCase().includes(s))
      .sort((a, b) => (b.start_time || "").localeCompare(a.start_time || ""));
  }, [classes, search]);

  const toggle = (id) => setSelected((sel) => (sel.includes(id) ? sel.filter((x) => x !== id) : [...sel, id]));
  const allChecked = sortedClasses.length > 0 && sortedClasses.every((c) => selected.includes(c.id));
  const toggleAll = () => setSelected(allChecked ? [] : sortedClasses.map((c) => c.id));
  const { page, setPage, pages, pageItems, total } = usePager(sortedClasses, 25);

  const bulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} class${selected.length > 1 ? "es" : ""} permanently?`)) return;
    const res = await Promise.allSettled(selected.map((id) => api.delete(`/classes/${id}`)));
    const ok = res.filter((r) => r.status === "fulfilled").length;
    if (ok) toast.success(`Deleted ${ok} class${ok > 1 ? "es" : ""}`);
    if (ok < selected.length) toast.error(`${selected.length - ok} could not be deleted`);
    setSelected([]);
    load();
  };

  const liveOrigin = typeof window !== "undefined" ? window.location.origin : "";
  // Invites ALWAYS use the teacher's permanent room link — students join as
  // guests through the waiting room, never a login/signup wall.
  const inviteLinkFor = () =>
    user?.profile_slug ? `${liveOrigin}/room/${user.profile_slug}` : liveOrigin;
  const copyInvite = async (c) => {
    try {
      await navigator.clipboard.writeText(inviteLinkFor(c));
      toast.success("Invitation link copied");
    } catch { toast.error("Copy failed — long-press to copy manually"); }
  };
  const whatsappInvite = (c) => {
    const txt = `You're invited to "${c.title}" on TeachingSoftware.ai · ${fmtDateTime(c.start_time)} · Join: ${inviteLinkFor(c)}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank");
  };
  const emailInvite = (c) => {
    const subject = encodeURIComponent(`Class invitation: ${c.title}`);
    const body = encodeURIComponent(`Hi,\n\nYou're invited to join the class "${c.title}".\n\nWhen: ${fmtDateTime(c.start_time)}\nJoin: ${inviteLinkFor(c)}\n\nSee you in class!`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const copyGuestLink = async (c) => {
    try {
      const { data } = await api.post(`/classes/${c.id}/guest-token`);
      const link = `${liveOrigin}/guest/${c.id}?token=${data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success("Guest invite link copied — anyone with it can join (no sign-up)");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not generate guest link");
    }
  };

  const events = useMemo(() => classes.map((c) => ({
    id: c.id,
    title: `${c.title} · ${c.subject}`,
    start: new Date(c.start_time),
    end: new Date(c.end_time),
    resource: c,
  })), [classes]);

  return (
    <div className="space-y-6" data-testid="classes-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ classes</div>
          <h1 className="font-display font-bold text-3xl tracking-tighter">{studentView ? "My classes" : "My Classes"}</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex border border-neutral-300 bg-white">
            <button
              data-testid="view-table-btn"
              onClick={() => setView("table")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "table" ? "bg-[#0A0C10] text-white" : "text-neutral-600 hover:bg-neutral-50"}`}
            >
              <List size={12} /> Table
            </button>
            <button
              data-testid="view-calendar-btn"
              onClick={() => setView("calendar")}
              className={`px-3 py-2 text-xs font-semibold inline-flex items-center gap-1.5 transition-colors ${view === "calendar" ? "bg-[#0A0C10] text-white" : "text-neutral-600 hover:bg-neutral-50"}`}
            >
              <CalendarDays size={12} /> Calendar
            </button>
          </div>
          {!studentView && (
            <>
              <button
                data-testid="start-your-class-btn"
                onClick={startInstantClass}
                disabled={startingInstant}
                className="inline-flex items-center gap-3 px-8 py-3.5 rounded-lg bg-[#00897B] hover:bg-[#00A48F] text-white font-bold text-base transition-all hover:shadow-[0_6px_24px_rgba(0,137,123,0.45)] hover:-translate-y-0.5 disabled:opacity-60"
              >
                <Zap size={19} />
                <span className="text-left leading-tight">
                  {startingInstant ? "Creating Meet…" : "Start your class"}
                  <span className="block text-[10px] font-semibold text-white/75 tracking-wide uppercase">Instant Google Meet</span>
                </span>
              </button>
              <CtaButton data-testid="create-class-btn" onClick={() => setShowCreate(true)} variant="primary" size="md" className="rounded-md">
                <Plus size={16} /> Schedule your class
              </CtaButton>
            </>
          )}
        </div>
      </div>

      {view === "calendar" ? (
        <div className="bg-white border border-neutral-200 p-3" data-testid="calendar-view">
          <BigCalendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 640 }}
            defaultView="week"
            views={["month", "week", "day", "agenda"]}
            eventPropGetter={(event) => {
              const s = event.resource?.status;
              const bg = s === "live" ? "#16a34a" : s === "completed" ? "#737373" : "#002FA7";
              return { style: { backgroundColor: bg, borderRadius: 0, border: 0, color: "white", padding: "2px 6px", fontSize: 12 } };
            }}
            onSelectEvent={(ev) => window.location.assign(`/live/${ev.id}`)}
          />
        </div>
      ) : (
      <div className="bg-white border border-neutral-200 overflow-x-auto">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search classes…" data-testid="classes-search"
              className="pl-8 pr-3 py-1.5 rounded-md border border-neutral-200 focus:border-[#0A0C10] outline-none text-xs w-56"
            />
          </div>
          {!studentView && selected.length > 0 && (
            <button onClick={bulkDelete} data-testid="classes-bulk-delete"
              className="inline-flex items-center gap-1.5 text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-50 px-3 py-1.5 rounded-md">
              <Trash2 size={12} /> Delete selected ({selected.length})
            </button>
          )}
          <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-neutral-400">{sortedClasses.length} class{sortedClasses.length === 1 ? "" : "es"}</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-neutral-50 text-left text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">
            <tr>
              {!studentView && <th className="px-4 py-3 w-8"><input type="checkbox" checked={allChecked} onChange={toggleAll} data-testid="classes-select-all" /></th>}
              <th className="px-5 py-3">Class</th>
              <th className="px-5 py-3">When</th>
              <th className="px-5 py-3">Platform</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-5 py-8 text-neutral-500">Loading…</td></tr>
            ) : sortedClasses.length === 0 ? (
              <tr><td colSpan={6} className="px-5 py-12 text-center text-neutral-500">{search ? "No classes match your search." : "No classes yet."}</td></tr>
            ) : pageItems.map((c) => (
              <tr key={c.id} className="border-t border-neutral-100">
                {!studentView && (
                  <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} data-testid={`class-check-${c.id}`} /></td>
                )}
                <td className="px-5 py-3">
                  <div className="font-medium">{c.title}</div>
                  <div className="text-xs text-neutral-500">{c.subject} · {c.class_type} · {(c.student_ids || []).length} students</div>
                </td>
                <td className="px-5 py-3 text-neutral-600">{fmtDateTime(c.start_time)}</td>
                <td className="px-5 py-3 text-neutral-600 capitalize">{platformLabel(c.meeting_platform)}</td>
                <td className="px-5 py-3"><span className={`text-xs px-2 py-0.5 ${c.status === "live" ? "bg-green-100 text-green-700" : c.status === "completed" ? "bg-neutral-100 text-neutral-600" : "bg-yellow-50 text-yellow-700"}`}>{c.status}</span></td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1">
                    <button
                      onClick={() => copyInvite(c)}
                      data-testid={`copy-invite-${c.id}`}
                      title="Copy invitation link"
                      className="text-xs px-2 py-1.5 border border-neutral-300 hover:border-[#0A0C10] hover:text-[#0A0C10] text-neutral-600 rounded-md"
                    >
                      <Link2 size={12} />
                    </button>
                    <button
                      onClick={() => whatsappInvite(c)}
                      data-testid={`wa-invite-${c.id}`}
                      title="Share via WhatsApp"
                      className="text-xs px-2 py-1.5 border border-neutral-300 hover:border-green-600 hover:text-green-600 text-neutral-600 rounded-md"
                    >
                      <MessageCircle size={12} />
                    </button>
                    <button
                      onClick={() => emailInvite(c)}
                      data-testid={`email-invite-${c.id}`}
                      title="Email invitation"
                      className="text-xs px-2 py-1.5 border border-neutral-300 hover:border-[#002FA7] hover:text-[#002FA7] text-neutral-600 rounded-md"
                    >
                      <Mail size={12} />
                    </button>
                    <button
                      onClick={() => copyGuestLink(c)}
                      data-testid={`guest-link-${c.id}`}
                      title="Copy public guest link (no sign-up needed)"
                      className="text-xs px-2 py-1.5 border border-neutral-300 hover:border-[#FFB400] hover:text-[#0A0C10] text-neutral-600 rounded-md"
                    >
                      <Share2 size={12} />
                    </button>
                    {c.meet_url && c.status !== "completed" && (
                      <button
                        onClick={() => {
                          if (!studentView && c.status === "live") {
                            openMeetTracked(c.meet_url, () => autoEndClass(c.id, { onDone: load }), c.id);
                          } else {
                            openMeetTracked(c.meet_url, null, c.id);
                          }
                        }}
                        data-testid={`open-meet-${c.id}`}
                        title="Open the Google Meet video call in a new tab"
                        className="text-xs font-semibold bg-[#00897B] text-white hover:bg-[#00A48F] rounded-md px-3 py-1.5 inline-flex items-center gap-1"
                      >
                        <Video size={12} /> Meet
                      </button>
                    )}
                    <Link to={`/live/${c.id}`} data-testid={`open-class-${c.id}`} className="text-xs font-semibold bg-[#0A0C10] text-[#FBFAF7] hover:bg-black rounded-md px-3 py-1.5 inline-flex items-center gap-1">
                      <DoorOpen size={12} /> Open
                    </Link>
                    {!studentView && (
                      <button onClick={() => setEditClass(c)} title="Edit date & time" data-testid={`edit-class-${c.id}`} className="text-xs px-2 py-1.5 border border-neutral-300 hover:border-[#0A0C10] hover:text-[#0A0C10] text-neutral-600 rounded-md"><Pencil size={12} /></button>
                    )}
                    {!studentView && <button onClick={() => del(c.id)} title="Delete" data-testid={`del-class-${c.id}`} className="text-xs px-2 py-1.5 border border-neutral-200 hover:border-red-500 text-red-500 rounded-md"><Trash2 size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <Pager page={page} pages={pages} total={total} onPage={setPage} testid="classes-pager" />
      </div>
      )}

      {showCreate && <CreateClassModal students={students} onStudentsChanged={load} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editClass && <EditClassModal cls={editClass} onClose={() => setEditClass(null)} onSaved={() => { setEditClass(null); load(); }} />}
      {instant && <InstantClassModal data={instant} onClose={() => setInstant(null)} onEnded={() => { setInstant(null); load(); }} />}
    </div>
  );
}

/* Edit a scheduled class's date & time (in the teacher's chosen time zone). */
function EditClassModal({ cls, onClose, onSaved }) {
  const { user } = useAuth();
  const [tz, setTz] = useState(user?.timezone || browserTz());
  const initial = utcToZoned(cls.start_time, user?.timezone || browserTz());
  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);
  const [saving, setSaving] = useState(false);

  const changeTz = (newTz) => {
    const z = utcToZoned(cls.start_time, newTz);
    setTz(newTz); setDate(z.date); setTime(z.time);
  };

  const save = async () => {
    if (!date || !time) return toast.error("Set both date and time");
    const iso = zonedToUtcIso(date, time, tz);
    if (!iso) return toast.error("Invalid date/time");
    setSaving(true);
    try {
      await api.patch(`/classes/${cls.id}`, { start_time: iso, duration_minutes: cls.duration_minutes || 60 });
      toast.success("Class rescheduled");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reschedule"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md p-6 rounded-lg" data-testid="edit-class-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ reschedule</div>
            <h2 className="font-display font-bold text-xl tracking-tight">{cls.title}</h2>
          </div>
          <button onClick={onClose} data-testid="edit-class-close"><X /></button>
        </div>
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500 inline-flex items-center gap-1"><Globe size={11} /> Time zone</div>
            <SearchableSelect testid="edit-class-tz" placeholder="Search time zone…" options={tzOptions()} value={tz} onChange={changeTz} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Date</span>
              <DateField value={date} onChange={setDate} testid="edit-class-date"
                className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-2 bg-white" />
            </label>
            <label className="block">
              <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-neutral-500">Time</span>
              <input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="edit-class-time"
                className="mt-1 w-full text-sm border border-neutral-300 rounded-md px-3 py-2 outline-none focus:border-[#0A0C10]" />
            </label>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md hover:border-black">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="edit-class-save"
            className="px-5 py-2 text-sm font-bold bg-[#0A0C10] text-white rounded-md hover:bg-black disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateClassModal({ students, onClose, onCreated, onStudentsChanged }) {
  const [form, setForm] = useState({
    title: "", subject: "", subject_id: "", batch_name: "",
    recording_enabled: true, reminders_enabled: true,
    student_ids: [], recurring: false,
  });
  // Per-day schedule: { monday: { date: "YYYY-MM-DD", time: "HH:MM" }, ... }
  const [days, setDays] = useState({});
  const [loading, setLoading] = useState(false);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [scheduled, setScheduled] = useState(null); // {count} → share screen
  const { user } = useAuth();
  const [tz, setTz] = useState(user?.timezone || browserTz());
  const roomLink = user?.profile_slug ? `${window.location.origin}/room/${user.profile_slug}` : window.location.origin;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleStudent = (id) => setForm((f) => ({ ...f, student_ids: f.student_ids.includes(id) ? f.student_ids.filter((x) => x !== id) : [...f.student_ids, id] }));

  const ORDERED_DAYS = useMemo(() => orderedDaysFor(tz), [tz]);
  const nextDateFor = (dayName) => {
    const { date, jsDay } = todayInTz(tz); // today in the SELECTED timezone
    const jsTarget = (DAYS.indexOf(dayName) + 1) % 7; // JS: sunday=0
    const delta = (jsTarget - jsDay + 7) % 7; // 0 = today
    const [y, m, dd] = date.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, dd + delta)).toISOString().slice(0, 10);
  };
  const toggleDay = (d) => setDays((cur) => {
    if (cur[d]) { const { [d]: _, ...rest } = cur; return rest; }
    return { ...cur, [d]: { date: nextDateFor(d), time: "16:00" } };
  });
  const setDayField = (d, field, value) => setDays((cur) => ({ ...cur, [d]: { ...cur[d], [field]: value } }));

  const submit = async (e) => {
    e.preventDefault();
    const sessions = [];
    for (const d of ORDERED_DAYS) {
      const s = days[d];
      if (!s) continue;
      if (!s.date || !s.time) return toast.error(`Set date & time for ${d}`);
      const iso = zonedToUtcIso(s.date, s.time, tz); // wall time in the selected tz → UTC
      if (!iso) return toast.error(`Invalid date/time for ${d}`);
      sessions.push({ date: iso.slice(0, 10), time: iso.slice(11, 16) });
    }
    if (!sessions.length) return toast.error("Select at least one day below and set its date & time");
    setLoading(true);
    try {
      const payload = {
        title: form.title,
        subject: form.subject,
        subject_id: form.subject_id,
        batch_name: form.batch_name,
        student_ids: form.student_ids,
        class_type: form.recurring ? "recurring" : "one_to_one",
        meeting_platform: "google_meet",
        duration_minutes: 60,
        recording_enabled: form.recording_enabled,
        reminders_enabled: form.reminders_enabled,
        sessions,
        recurring: form.recurring,
      };
      const { data } = await api.post("/classes", payload);
      toast.success(`Scheduled ${data.created} class${data.created > 1 ? "es" : ""}${data.meet_url ? " — Google Meet link created" : ""}`);
      if (data.meet_warning && !data.meet_url) toast.warning(`No Meet link: ${data.meet_warning}`, { duration: 8000 });
      setScheduled({ count: data.created, title: form.title, meetUrl: data.meet_url || "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  // ── Post-schedule share screen: copy / WhatsApp / Email the Google Meet link
  if (scheduled) {
    const shareLink = scheduled.meetUrl || roomLink;
    const waText = encodeURIComponent(`Hi! Your class "${scheduled.title}" is scheduled. Join on Google Meet: ${shareLink}`);
    return (
      <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md p-6 rounded-lg" data-testid="class-share-screen">
          <div className="text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center"><Zap size={20} /></div>
            <h2 className="font-display font-bold text-2xl tracking-tight mt-3">Class scheduled!</h2>
            <p className="text-sm text-neutral-500 mt-1">
              {scheduled.count} session{scheduled.count > 1 ? "s" : ""} created.
              {scheduled.meetUrl
                ? " A Google Meet link was auto-created — share it with your students. It opens in a new tab."
                : " Share your classroom link — students join without creating an account."}
            </p>
          </div>
          <div className="mt-4 flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-md px-3 py-2.5">
            <Link2 size={13} className="text-neutral-400 shrink-0" />
            <span className="text-xs font-mono truncate flex-1" data-testid="share-meet-url">{shareLink}</span>
            <button onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Link copied"); }} data-testid="share-copy-link"
              className="text-xs font-bold px-2.5 py-1.5 rounded-md bg-[#0A0C10] text-white hover:bg-black shrink-0 inline-flex items-center gap-1"><Copy size={11} /> Copy</button>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <a href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
              className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md py-2.5 bg-[#25D366] text-white hover:brightness-105">
              <MessageCircle size={15} /> WhatsApp
            </a>
            <a href={`mailto:?subject=${encodeURIComponent(`Class scheduled: ${scheduled.title}`)}&body=${waText}`} data-testid="share-email"
              className="inline-flex items-center justify-center gap-2 text-sm font-bold rounded-md py-2.5 border border-neutral-300 hover:border-[#0A0C10]">
              <Mail size={15} /> Email
            </a>
          </div>
          <button onClick={onCreated} data-testid="share-done"
            className="mt-3 w-full text-sm font-semibold text-neutral-500 hover:text-black py-2">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-2xl p-6 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ new class</div>
            <h2 className="font-display font-bold text-2xl tracking-tight">Schedule a class</h2>
          </div>
          <button onClick={onClose}><X /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <input className={inp} placeholder="Class title *" required value={form.title} onChange={set("title")} data-testid="class-title" />
            <SubjectPicker
              testid="class-subject"
              className="rounded-md"
              value={form.subject_id}
              onChange={(sid, sname) => setForm({ ...form, subject_id: sid, subject: sname })}
            />
          </div>

          <label className="flex items-center gap-2.5 border border-neutral-200 rounded-md px-3 py-2.5 cursor-pointer hover:border-neutral-400" data-testid="recurring-class-toggle">
            <input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} />
            <span className="text-sm font-medium inline-flex items-center gap-1.5"><Repeat size={13} /> Recurring Class</span>
            <span className="text-xs text-neutral-500 ml-auto">{form.recurring ? "Repeats weekly on the selected days" : "One-off — runs once on each selected day"}</span>
          </label>

          {/* Day-based scheduler — pick days, then set a date & time per day */}
          <div className="bg-neutral-50 border border-neutral-200 p-3 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500"><Calendar size={12} className="inline" /> Class days</div>
              <div className="flex items-center gap-1.5 min-w-[220px]" data-testid="class-timezone-picker">
                <Globe size={12} className="text-neutral-400 shrink-0" />
                <div className="flex-1">
                  <SearchableSelect testid="class-timezone" placeholder="Time zone…" options={tzOptions()} value={tz} onChange={setTz} />
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {ORDERED_DAYS.map((d, i) => (
                <button type="button" key={d}
                  data-testid={`day-${d}`}
                  onClick={() => toggleDay(d)}
                  className={`text-xs px-3 py-1.5 border rounded-md transition-colors ${days[d] ? "bg-[#0A0C10] text-white border-transparent" : "border-neutral-300 hover:border-neutral-500"}`}
                >{d.slice(0, 3).toUpperCase()}{i === 0 ? " · today" : ""}</button>
              ))}
            </div>
            {ORDERED_DAYS.filter((d) => days[d]).map((d) => (
              <div key={d} className="flex items-center gap-2 bg-white border border-neutral-200 rounded-md px-3 py-2" data-testid={`day-schedule-${d}`}>
                <span className="text-xs font-semibold capitalize w-24">{d}</span>
                <DateField className="text-xs border border-neutral-200 rounded px-2 py-1.5 bg-white"
                  value={days[d].date} onChange={(v) => setDayField(d, "date", v)} testid={`day-date-${d}`} />
                <input type="time" className="text-xs border border-neutral-200 rounded px-2 py-1.5 outline-none focus:border-[#0A0C10]"
                  value={days[d].time} onChange={(e) => setDayField(d, "time", e.target.value)} data-testid={`day-time-${d}`} />
                <span className="ml-auto text-[10px] font-mono uppercase tracking-wider text-neutral-400">{form.recurring ? "weekly" : "once"}</span>
              </div>
            ))}
            <p className="text-[11px] text-neutral-500">A Google Meet link is auto-created when you schedule — students get it by email and in their dashboard, and it opens in a new tab. Reminders go out 15 minutes before start.</p>
          </div>

          <input className={inp} placeholder="Batch name (optional — eg: Grade 9 — A)" value={form.batch_name} onChange={set("batch_name")} />

          {students.length > 0 ? (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">Assign students</div>
                <button type="button" data-testid="inline-add-student" onClick={() => setShowAddStudent(true)} className="text-xs inline-flex items-center gap-1 text-[#0A0C10] hover:underline">
                  <UserPlus size={12} /> Add new
                </button>
              </div>
              <div className="max-h-32 overflow-y-auto border border-neutral-200 p-2 grid sm:grid-cols-2 gap-1">
                {students.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm px-2 py-1 hover:bg-neutral-50">
                    <input type="checkbox" checked={form.student_ids.includes(s.id)} onChange={() => toggleStudent(s.id)} />
                    {s.full_name}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between bg-neutral-50 border border-dashed border-neutral-300 rounded-md p-3">
              <div className="text-xs text-neutral-500">No students yet — add one or schedule and share a guest link after.</div>
              <button type="button" data-testid="inline-add-student" onClick={() => setShowAddStudent(true)} className="text-xs inline-flex items-center gap-1 px-2 py-1.5 border border-neutral-300 hover:border-[#0A0C10] rounded-md">
                <UserPlus size={12} /> Add student
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-4 text-sm">
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.recording_enabled} onChange={(e) => setForm({ ...form, recording_enabled: e.target.checked })} /> Recording</label>
            <label className="inline-flex items-center gap-2"><input type="checkbox" checked={form.reminders_enabled} onChange={(e) => setForm({ ...form, reminders_enabled: e.target.checked })} /> Auto reminders</label>
          </div>

          <CtaButton data-testid="class-submit" type="submit" disabled={loading} variant="primary" size="lg" className="w-full rounded-md">
            <Calendar size={14} /> {loading ? "Scheduling..." : "Schedule class"}
          </CtaButton>
        </form>
        {showAddStudent && (
          <InlineAddStudent
            onClose={() => setShowAddStudent(false)}
            onAdded={(sid) => {
              setShowAddStudent(false);
              // Auto-select the newly added student
              if (sid) setForm((f) => ({ ...f, student_ids: f.student_ids.includes(sid) ? f.student_ids : [...f.student_ids, sid] }));
              onStudentsChanged && onStudentsChanged();
            }}
          />
        )}
      </div>
    </div>
  );
}

function InlineAddStudent({ onClose, onAdded }) {
  const [form, setForm] = useState({ full_name: "", email: "", grade: "", parent_email: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) return toast.error("Name + email are required");
    setBusy(true);
    try {
      const { data } = await api.post("/students", form);
      toast.success(`Student added · temp password: ${data.invite_password || "(existing)"}`);
      onAdded(data.student_id);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} data-testid="inline-add-student-modal">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">/ quick add</div>
            <h3 className="font-display font-bold text-xl">Add student</h3>
          </div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <input className={inp} placeholder="Full name *" required value={form.full_name} onChange={set("full_name")} data-testid="inline-student-name" />
          <input className={inp} type="email" placeholder="Email (optional)" value={form.email} onChange={set("email")} data-testid="inline-student-email" />
          <input className={inp} placeholder="Grade (optional)" value={form.grade} onChange={set("grade")} data-testid="inline-student-grade" />
          <input className={inp} type="email" placeholder="Parent email (optional)" value={form.parent_email} onChange={set("parent_email")} data-testid="inline-student-parent" />
          <p className="text-[11px] text-neutral-500">Student gets temp password <strong>Ts@1234</strong> and is forced to change on first login.</p>
          <CtaButton type="submit" disabled={busy} variant="primary" size="md" className="w-full rounded-md" data-testid="inline-student-submit">
            <UserPlus size={14} /> {busy ? "Adding…" : "Add student"}
          </CtaButton>
        </form>
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";

